import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * Switch, the 46 by 28 toggle track.
 *
 * CSS selector: `mobile/atoms.css` `.ds-switch` (+ `--on`, plus the
 * `[aria-checked="true"]` and `:checked` hooks the CSS also honours).
 *
 * Doctrine: accent marks control state, so the on track is accent-filled and
 * the thumb flips to the on-accent dark glyph colour. Ink stays out of it.
 *
 * Rendered as a `role="switch"` button rather than a hidden native input,
 * because a toggle that is not part of a form submission is an ARIA switch.
 * The CSS keys off `[aria-checked="true"]` so no extra class is needed.
 *
 * `presentational` renders an inert span carrying `.ds-switch--on` instead, for
 * use inside another interactive element such as `<SwitchRow>`, where a nested
 * button would be invalid markup and the row already owns the toggle semantics.
 */
export const Switch = React.forwardRef(function Switch(
  {
    className,
    checked = false,
    onCheckedChange,
    presentational = false,
    type = 'button',
    onClick,
    ...rest
  },
  ref,
) {
  if (presentational) {
    return (
      <span
        aria-hidden="true"
        className={cn('ds-switch', checked && 'ds-switch--on', className)}
      />
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      role="switch"
      aria-checked={checked}
      onClick={(event) => {
        if (onClick) onClick(event);
        if (onCheckedChange) onCheckedChange(!checked);
      }}
      className={cn('ds-switch', className)}
      {...rest}
    />
  );
});

export default Switch;
